import type {
  EntryModule,
  LocatedUnknown,
  ScenarioEntry,
  ScenarioProjection,
  ScenarioProvenance,
  SourceObject,
  SourceValue,
} from './types';
export function isObject(value: unknown): value is SourceObject {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}
export function freezeSource<T>(value: T): T {
  if (value && typeof value === 'object') {
    Object.values(value).forEach(freezeSource);
    Object.freeze(value);
  }
  return value;
}
export function locatedUnknowns(value: SourceValue, path = ''): readonly LocatedUnknown[] {
  if (Array.isArray(value))
    return value.flatMap((item, index) => locatedUnknowns(item, path + '/' + index));
  if (!isObject(value)) return [];
  if (value.status === 'UNKNOWN') return [{ path, value }];
  return Object.entries(value).flatMap(([key, item]) => locatedUnknowns(item, path + '/' + key));
}
function rows(value: SourceValue | undefined): readonly SourceObject[] {
  if (!Array.isArray(value) || !value.every(isObject)) throw new Error('Source rows unavailable');
  return value;
}
function text(row: SourceObject, key: string): string {
  const value = row[key];
  if (typeof value !== 'string') throw new Error('Source field unavailable: ' + key);
  return value;
}
/** Stable source order; no severity, inferred availability or priority. */
export function projectScenario(
  body: SourceObject,
  provenance: ScenarioProvenance,
): ScenarioProjection {
  if (
    !isObject(body.identity) ||
    body.identity.name !== 'High-Rise Building Fire' ||
    !isObject(body.riskModel) ||
    !isObject(body.unknownDeclarations)
  )
    throw new Error('Source identity unavailable');
  const relations = rows(body.riskModel.relationships);
  const specifications: readonly [EntryModule, readonly SourceObject[], string, string, string][] =
    [
      ['risks', rows(body.riskModel.hazards), 'hazardCode', 'hazardClass', 'hazardClass'],
      [
        'information',
        rows(body.informationRequirement),
        'requirementId',
        'whatMustBeKnown',
        'dimension',
      ],
      [
        'capability',
        rows(body.capabilityRequirement),
        'capabilityTypeCode',
        'capabilityClass',
        'effectCode',
      ],
      ['machine', rows(body.machineFirstRule), 'ruleCode', 'taskClass', 'areaClass'],
      ['products', rows(body.productMapping), 'problemId', 'problem', 'problem'],
    ];
  const entries = Object.fromEntries(
    specifications.map(([module, source, codeKey, nameKey, categoryKey]) => [
      module,
      source.map((row, index): ScenarioEntry => ({
        code: text(row, codeKey),
        name: text(row, nameKey),
        category: text(row, categoryKey),
        sourcePath:
          (module === 'risks'
            ? '/riskModel/hazards'
            : module === 'information'
              ? '/informationRequirement'
              : module === 'capability'
                ? '/capabilityRequirement'
                : module === 'machine'
                  ? '/machineFirstRule'
                  : '/productMapping') +
          '/' +
          index,
        source: row,
        unknowns: locatedUnknowns(row),
        related:
          module === 'risks'
            ? relations.filter(
                (r) =>
                  r.sourceHazard === row[codeKey] ||
                  (Array.isArray(r.targets) && r.targets.includes(row[codeKey] ?? null)),
              )
            : module === 'capability'
              ? rows(body.productMapping).filter(
                  (r) =>
                    Array.isArray(r.requiredCapabilityTypeCode) &&
                    r.requiredCapabilityTypeCode.includes(row[codeKey] ?? null),
                )
              : [],
      })),
    ]),
  ) as unknown as Record<EntryModule, readonly ScenarioEntry[]>;
  return freezeSource({
    body,
    provenance,
    entries,
    unknowns: locatedUnknowns(body).filter((u) => !u.path.startsWith('/unknownDeclarations/')),
    unknownRegister: locatedUnknowns(body.unknownDeclarations, '/unknownDeclarations'),
  });
}
